import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { BatchConsumerResponseModel } from './models/batch-consumer-response-model';

@Injectable({
  providedIn: 'root',
})
export class BatchConsumersAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryString);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const requestSourceFilter = this.getStateFilterValue(filter, 'requestSource');
        if (requestSourceFilter != undefined && requestSourceFilter.length >= 3) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}requestSource=${encodeURIComponent(requestSourceFilter)}`);
        }

        const serviceBusNamespaceFilter = this.getStateFilterValue(filter, 'serviceBusNamespace');
        if (serviceBusNamespaceFilter != undefined && serviceBusNamespaceFilter.length >= 3) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}serviceBusNamespace=${encodeURIComponent(serviceBusNamespaceFilter)}`);
        }

        const serviceBusQueueFilter = this.getStateFilterValue(filter, 'serviceBusQueue');
        if (serviceBusQueueFilter != undefined && serviceBusQueueFilter.length >= 3) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}serviceBusQueue=${encodeURIComponent(serviceBusQueueFilter)}`);
        }

        const eventTypeSuffixFilter = this.getStateFilterValue(filter, 'eventTypeSuffix');
        if (eventTypeSuffixFilter != undefined && eventTypeSuffixFilter.length >= 3) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}eventTypeSuffix=${encodeURIComponent(eventTypeSuffixFilter)}`);
        }

        const isActiveFilter = this.getStateFilterValue(filter, 'isActive');
        if (isActiveFilter != undefined) {
            queryStart = this.getQueryStart(queryString);
            queryString = queryString.concat(`${queryStart}isActive=${encodeURIComponent(isActiveFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `batchconsumers/search` + queryString)
          .pipe(
              map(
                  (response: GridDataResult) => {
                      return {
                          data: response.data,
                          total: response.total
                      };
                  }
              ), tap(() => (this.loading = false))
          );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }
}

@Injectable()
export class BatchConsumersService extends BatchConsumersAPIService {
    constructor(http: HttpClient) {
        super(http);
    }

    public create(model: BatchConsumerResponseModel): Observable<BatchConsumerResponseModel> {
        return this.http.post<BatchConsumerResponseModel>(this.baseUrlLocal + `batchconsumers`, model);
    }

    public update(requestSource: string, model: BatchConsumerResponseModel): Observable<BatchConsumerResponseModel> {
        return this.http.put<BatchConsumerResponseModel>(this.baseUrlLocal + `batchconsumers/${btoa(requestSource)}`, model);
    }

    public delete(requestSource: string): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `batchconsumers/${btoa(requestSource)}`);
    }
}
